import { Chip, resetButtonStyle, TextButton, Typography } from "matsuri-ui"
import { Link } from "react-router-dom"
import { css } from "@emotion/react"

import { useMemo } from "react"
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew"
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos"
import dayjs from "dayjs"
import { theme } from "../../theme"
import { displayWeekdayJa } from "../../utils/displayWeekday"
import { MobileMenu } from "../../templates/MobileMenu"
import { useSalesByDate } from "./useSalesByDate"
import { useStartOfWeekQuery } from "@/parts/Achievement/useStartOfWeekQuery"
import { showJpYen } from "@/utils/showJpYen"
import { formatDate } from "@/utils/dateString"
import { AchivementSales } from "@/parts/Achievement/sales/types"
import { useSimpleCleaningsByDates } from "@/hooks/useSimpleCleaning"
import { extractAchievedCleanings } from "@/model/simpleCleaning"
import { useTranslation } from "@/hooks/useTranslation"

const styles = {
    listHeader: css`
        padding: 8px 60px 8px 20px;
        border-bottom: 1px solid ${theme.palette.intentions.cleanerGray.light};
    `,
    listItem: css`
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 8px;
        justify-content: inherit;
        padding: 8px 20px;
        border-bottom: 1px solid ${theme.palette.intentions.cleanerGray.light};
    `,
    listItemContent: css`
        display: grid;
        grid-template-columns: 80px 50px auto 50px;
        gap: 16px;
    `
}

export const WeeklyAchievementPage = () => {
    const { t } = useTranslation()
    const { startOfWeek, onChange } = useStartOfWeekQuery()

    const startDate = dayjs(startOfWeek)
    const setStartDate = (newStartDate: dayjs.Dayjs) => {
        onChange(formatDate(newStartDate))
    }

    const dateInWeek = Array.from({ length: 7 }).map((_, i) =>
        startDate.add(i, "day")
    )

    const dateStringInWeek = dateInWeek.map(formatDate)
    const { data: cleaningsByDate } =
        useSimpleCleaningsByDates(dateStringInWeek)

    const { salesByDate } = useSalesByDate({ dateStringInWeek })

    const achievementList: AchievementItem[] = useMemo(() => {
        if (!cleaningsByDate) return []

        return dateInWeek.map(date => {
            const dateString = formatDate(date)

            const cleanings = extractAchievedCleanings(
                cleaningsByDate[dateString] ?? []
            )

            return {
                date,
                count: cleanings.length,
                sales: salesByDate[dateString] ?? 0
            }
        })
    }, [cleaningsByDate, dateInWeek, salesByDate])

    const countTotal = achievementList.reduce((acc, cur) => acc + cur.count, 0)

    return (
        <div>
            <header
                css={css`
                    display: grid;
                    padding-top: env(safe-area-inset-top, 0px);
                    background-color: ${
                        theme.palette.intentions.cleanerBackground.main
                    };
                `}
            >
                <div>
                    <MobileMenu />
                </div>
                <div
                    css={css`
                        display: grid;
                        grid-template-columns: auto 1fr auto;
                        gap: 8px;
                        padding: 8px 16px;
                    `}
                >
                    <TextButton
                        icon={<ArrowBackIosNewIcon />}
                        onClick={() =>
                            setStartDate(startDate.subtract(7, "day"))
                        }
                    />
                    <Typography
                        bolder
                        css={css`
                            font-size: 20px;
                            text-align: center;
                        `}
                    >
                        {t(
                            `${startDate.format("M/D")}-${startDate.add(6, "day").format("M/D")}の実績`
                        )}
                    </Typography>
                    <TextButton
                        icon={<ArrowForwardIosIcon />}
                        onClick={() => setStartDate(startDate.add(7, "day"))}
                    />
                </div>
            </header>

            {countTotal > 0 ? (
                <div
                    css={css`
                        display: grid;
                        padding-bottom: 34px; // iOSインジケーター分
                    `}
                >
                    <div css={styles.listHeader}>
                        <div css={styles.listItemContent}>
                            <Chip
                                css={css`
                                    display: flex;
                                    justify-content: center;
                                    font-size: 12px;
                                `}
                            >
                                {t("日付")}
                            </Chip>
                            <Chip>{t("件数")}</Chip>
                            {/* 余白のためのdiv定義 */}
                            <div />
                            <Chip>{t("売上")}</Chip>
                        </div>
                    </div>

                    <AchievementList achievementList={achievementList} />
                </div>
            ) : (
                <Typography
                    css={css`
                        display: grid;
                        justify-content: center;
                        margin-top: 8px;
                        font-size: 16px;
                    `}
                >
                    {t("実績はありません。")}
                </Typography>
            )}
        </div>
    )
}

interface AchievementItem {
    date: dayjs.Dayjs
    count: number
    sales: AchivementSales
}

const AchievementList = ({
    achievementList
}: {
    achievementList: AchievementItem[]
}) => {
    const { t } = useTranslation()
    return achievementList.map(item => (
        <Link
            aria-disabled={item.count === 0}
            to={`/achievements/${item.date.format("YYYY-MM-DD")}`}
            css={[resetButtonStyle, styles.listItem]}
            key={item.date.format("YYYY-MM-DD")}
            onClick={event => {
                if (item.count === 0) {
                    event.preventDefault()
                }
            }}
        >
            <div css={styles.listItemContent}>
                <Typography
                    css={css`
                        font-size: 16px;
                    `}
                >
                    {item.date.format("M/DD")}(
                    {t(displayWeekdayJa(item.date.day()))})
                </Typography>
                <Typography
                    css={css`
                        font-size: 16px;
                    `}
                >
                    {t(`${item.count}件`)}
                </Typography>
                {/* 余白のためのdiv定義 */}
                <div />
                <div
                    css={css`
                        display: flex;
                        justify-content: flex-end;
                    `}
                >
                    {item.count > 0 ? <SaleColumn sales={item.sales} /> : null}
                </div>
            </div>
            <div
                css={css`
                    display: flex;
                    justify-content: flex-end;
                `}
            >
                {item.count > 0 && (
                    <TextButton icon={<ArrowForwardIosIcon />} />
                )}
            </div>
        </Link>
    ))
}

const SaleColumn = ({ sales }: { sales: AchivementSales }) => {
    return (
        <Typography
            css={css`
                font-weight: bold;
            `}
        >
            {showJpYen(sales)}
        </Typography>
    )
}
